import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
	Avatar,
	Button,
	Chip,
	IconButton,
	MenuItem,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
	Add,
	ChatBubbleOutline,
	Edit,
	LabelOutlined,
	MoreVert,
	Refresh,
	Schedule,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const UNTAGGED_COLUMN = "sin-etiqueta";

const useStyles = makeStyles((theme) => ({
	root: {
		height: "100%",
		display: "flex",
		flexDirection: "column",
		background: theme.palette.type === "dark" ? "#102925" : "#f6f8fc",
		color: theme.palette.text.primary,
		overflow: "hidden",
	},
	toolbar: {
		display: "grid",
		gridTemplateColumns: "minmax(190px, 1fr) auto minmax(190px, 1fr)",
		alignItems: "center",
		gap: theme.spacing(2),
		padding: theme.spacing(1.5, 2),
		borderBottom: `1px solid ${theme.palette.divider}`,
		"@media (max-width: 760px)": {
			gridTemplateColumns: "1fr auto",
			"& > :nth-child(2)": { gridColumn: "1 / -1", gridRow: 1, justifySelf: "center" },
		},
	},
	viewSelect: { minWidth: 178 },
	workflow: {
		textAlign: "center",
		fontWeight: 700,
		letterSpacing: 0.2,
		color: theme.palette.text.secondary,
		borderBottom: `1px solid ${theme.palette.primary.main}`,
		paddingBottom: 7,
		minWidth: 190,
	},
	actions: { display: "flex", justifyContent: "flex-end", alignItems: "center", gap: theme.spacing(0.5) },
	board: {
		flex: 1,
		display: "flex",
		gap: theme.spacing(1.5),
		overflowX: "auto",
		overflowY: "hidden",
		padding: theme.spacing(2),
		minHeight: 0,
		cursor: "grab",
		userSelect: "none",
		"&::-webkit-scrollbar": { height: 10 },
	},
	column: {
		width: 330,
		minWidth: 330,
		display: "flex",
		flexDirection: "column",
		minHeight: 0,
		padding: theme.spacing(1.25),
		borderRadius: 14,
		background: theme.palette.type === "dark" ? "#173a35" : "#ffffff",
		border: `1px solid ${theme.palette.divider}`,
		boxShadow: theme.palette.type === "dark" ? "none" : "0 8px 20px rgba(20, 43, 75, .06)",
	},
	columnHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		padding: theme.spacing(0.4, 0.5, 1.1),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	columnTitle: { display: "flex", alignItems: "center", gap: theme.spacing(0.8), minWidth: 0 },
	columnName: { fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
	count: {
		minWidth: 29,
		height: 29,
		padding: theme.spacing(0, 0.8),
		borderRadius: 15,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		fontWeight: 800,
		color: theme.palette.primary.contrastText,
		background: theme.palette.primary.main,
	},
	cards: { flex: 1, overflowY: "auto", paddingTop: theme.spacing(1), minHeight: 100 },
	card: {
		position: "relative",
		padding: theme.spacing(1.35),
		marginBottom: theme.spacing(1),
		borderRadius: 12,
		background: theme.palette.type === "dark" ? "#1d4540" : "#fff",
		border: `1px solid ${theme.palette.divider}`,
		cursor: "grab",
		transition: "border-color .15s ease, box-shadow .15s ease, transform .15s ease",
		"&:hover": { borderColor: theme.palette.primary.main, boxShadow: "0 5px 16px rgba(27, 94, 151, .14)", transform: "translateY(-1px)" },
	},
	liveCard: {
		background: theme.palette.type === "dark" ? "#1a4b46" : "#f1f8ff",
		borderLeft: `4px solid ${theme.palette.primary.main}`,
	},
	cardTop: { display: "flex", alignItems: "flex-start", gap: theme.spacing(1), minWidth: 0 },
	avatar: { width: 42, height: 42, background: theme.palette.primary.main, fontWeight: 800 },
	cardText: { minWidth: 0, flex: 1 },
	cardName: { fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
	preview: { marginTop: 3, color: theme.palette.text.secondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
	meta: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: theme.spacing(1), marginTop: theme.spacing(1) },
	channel: { maxWidth: 190, height: 24, fontSize: 11, fontWeight: 700, borderRadius: 6 },
	date: { display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", fontSize: 12, color: theme.palette.text.secondary },
	unread: { minWidth: 24, height: 24, borderRadius: 12, fontWeight: 800, color: "#fff", background: "#ef4444", fontSize: 12 },
	addForm: { paddingTop: theme.spacing(0.5), borderTop: `1px dashed ${theme.palette.divider}` },
	addRow: { display: "flex", alignItems: "center", gap: 4, marginTop: theme.spacing(0.5) },
	addButton: { flexShrink: 0 },
	empty: { padding: theme.spacing(5, 2), textAlign: "center", color: theme.palette.text.secondary },
	muted: { color: theme.palette.text.secondary },
}));

const initials = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).map((word) => word[0]).join("").toUpperCase() || "CL";

const formatTime = (value) => {
	if (!value) return "";
	try {
		return new Intl.DateTimeFormat("es-UY", { timeZone: "America/Montevideo", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }).format(new Date(value));
	} catch (_) { return ""; }
};

const Kanban = () => {
	const classes = useStyles();
	const history = useHistory();
	const [columns, setColumns] = useState([]);
	const [cards, setCards] = useState([]);
	const [tickets, setTickets] = useState([]);
	const [newCard, setNewCard] = useState({});
	const [newCardContact, setNewCardContact] = useState({});
	const [contacts, setContacts] = useState([]);
	const [view, setView] = useState("unread");
	const { isAuth } = useContext(AuthContext);
	const boardDrag = useRef({ active: false, startX: 0, scrollLeft: 0 });

	const loadBoard = async () => {
		try {
			const { data } = await api.get("/crm/kanban");
			setColumns(data.columns || []);
			setCards(data.cards || []);
			setTickets(data.tickets || []);
		} catch (error) { toastError(error); }
	};

	useEffect(() => { if (isAuth) loadBoard(); }, [isAuth]);
	useEffect(() => {
		if (!isAuth) return;
		api.get("/crm/contact-options").then(({ data }) => setContacts(data.contacts || [])).catch(toastError);
	}, [isAuth]);

	const liveCards = useMemo(() => {
		const linkedTicketIds = new Set(cards.map((card) => String(card.ticketId)).filter(Boolean));
		return tickets
			.filter((ticket) => !linkedTicketIds.has(String(ticket.id)))
			.filter((ticket) => view !== "unread" || Number(ticket.unreadMessages || 0) > 0)
			.map((ticket) => {
				const matchingColumn = columns.find((column) => (ticket.labels || []).some((label) => String(label).toLocaleLowerCase("es-UY") === String(column.name).toLocaleLowerCase("es-UY")));
				return {
					id: `ticket:${ticket.id}`,
					ticketId: ticket.id,
					columnId: matchingColumn ? matchingColumn.id : UNTAGGED_COLUMN,
					title: ticket.contact?.name || "Cliente",
					contactName: ticket.contact?.name || "",
					contactId: ticket.contact?.id || null,
					details: ticket.preview || "Sin mensaje reciente",
					channel: ticket.channel || "Chatwoot",
					channelType: ticket.channelType || "",
					lastMessageAt: ticket.lastMessageAt || ticket.updatedAt,
					unreadMessages: ticket.unreadMessages || 0,
					profilePicUrl: ticket.contact?.profilePicUrl || "",
					isLiveConversation: true,
				};
			});
	}, [cards, columns, tickets, view]);

	const displayedCards = useMemo(() => [...cards, ...liveCards], [cards, liveCards]);
	const boardColumns = useMemo(() => [{ id: UNTAGGED_COLUMN, name: "Todos", fixed: true }, ...columns], [columns]);

	const createCard = async (event, columnId) => {
		event.preventDefault();
		const contact = contacts.find((item) => String(item.id) === String(newCardContact[columnId] || ""));
		const title = (newCard[columnId] || contact?.name || "").trim();
		if (!title) return;
		try {
			await api.post("/crm/kanban/cards", { title, columnId, contactId: contact?.id || null, contactName: contact?.name || "", ticketId: contact?.ticketId || null });
			setNewCard({ ...newCard, [columnId]: "" });
			setNewCardContact({ ...newCardContact, [columnId]: "" });
			await loadBoard();
		} catch (error) { toastError(error); }
	};

	const moveCard = async (cardId, columnId) => {
		if (columnId === UNTAGGED_COLUMN) return;
		const liveCard = liveCards.find((card) => String(card.id) === String(cardId));
		try {
			if (liveCard) {
				await api.post("/crm/kanban/cards", {
					title: liveCard.title,
					details: liveCard.details,
					columnId,
					contactId: liveCard.contactId,
					contactName: liveCard.contactName,
					ticketId: liveCard.ticketId,
				});
				toast.success("Cliente incorporado al flujo y etiqueta sincronizada.");
				await loadBoard();
				return;
			}
			const { data } = await api.put(`/crm/kanban/cards/${cardId}`, { columnId });
			setCards((current) => current.map((card) => Number(card.id) === Number(cardId) ? data : card));
		} catch (error) { toastError(error); }
	};

	const editColumn = async (column) => {
		const name = window.prompt("Nombre de la etiqueta/columna", column.name);
		if (!name || name.trim() === column.name) return;
		try {
			await api.put(`/crm/kanban/columns/${column.id}`, { name: name.trim() });
			await loadBoard();
		} catch (error) { toastError(error); }
	};

	const addColumn = async () => {
		const name = window.prompt("Nombre de la nueva etiqueta/columna");
		if (!name?.trim()) return;
		try {
			await api.post("/crm/kanban/columns", { name: name.trim() });
			await loadBoard();
		} catch (error) { toastError(error); }
	};

	const beginBoardPan = (event) => {
		if (event.button !== 0 || event.target.closest("[draggable='true'], button, input, textarea")) return;
		const board = event.currentTarget;
		boardDrag.current = { active: true, startX: event.clientX, scrollLeft: board.scrollLeft };
		board.setPointerCapture?.(event.pointerId);
		board.style.cursor = "grabbing";
	};
	const panBoard = (event) => { if (boardDrag.current.active) event.currentTarget.scrollLeft = boardDrag.current.scrollLeft - (event.clientX - boardDrag.current.startX); };
	const endBoardPan = (event) => {
		if (!boardDrag.current.active) return;
		boardDrag.current.active = false;
		event.currentTarget.style.cursor = "grab";
		if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
	};

	const renderCard = (card) => <Paper
		key={card.id}
		draggable
		variant="outlined"
		className={`${classes.card} ${card.isLiveConversation ? classes.liveCard : ""}`}
		onDragStart={(event) => { event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", String(card.id)); }}
	>
		<div className={classes.cardTop}>
			<Avatar src={card.profilePicUrl} className={classes.avatar}>{initials(card.contactName || card.title)}</Avatar>
			<div className={classes.cardText}>
				<Typography className={classes.cardName}>{card.contactName || card.title}</Typography>
				<Typography variant="body2" className={classes.preview}>{card.details || card.title}</Typography>
			</div>
			{card.unreadMessages > 0 && <Chip label={card.unreadMessages > 9 ? "9+" : card.unreadMessages} className={classes.unread} />}
			{card.ticketId && <Tooltip title="Abrir conversación"><IconButton size="small" aria-label={`Abrir conversación de ${card.contactName || card.title}`} onClick={() => history.push(`/tickets/${card.ticketId}`)}><MoreVert className={classes.muted} fontSize="small" /></IconButton></Tooltip>}
		</div>
		<div className={classes.meta}>
			<Chip icon={<ChatBubbleOutline />} label={card.channel || (card.isLiveConversation ? "Chatwoot" : "Tarea") } className={classes.channel} color="primary" />
			<span className={classes.date}><Schedule fontSize="inherit" />{formatTime(card.lastMessageAt || card.updatedAt || card.createdAt)}</span>
		</div>
	</Paper>;

	return <MainContainer>
		<div className={classes.root}>
			<div className={classes.toolbar}>
				<TextField select size="small" variant="outlined" value={view} onChange={(event) => setView(event.target.value)} className={classes.viewSelect}>
					<MenuItem value="unread">Sin atender</MenuItem>
					<MenuItem value="all">Todas las conversaciones</MenuItem>
				</TextField>
				<Typography className={classes.workflow}>Flujo de trabajo</Typography>
				<div className={classes.actions}>
					<Tooltip title="Actualizar clientes y etiquetas"><IconButton color="primary" aria-label="Actualizar Kanban" onClick={loadBoard}><Refresh /></IconButton></Tooltip>
					<Button startIcon={<Add />} color="primary" variant="contained" onClick={addColumn}>Nueva etapa</Button>
				</div>
			</div>
			<div className={classes.board} onPointerDown={beginBoardPan} onPointerMove={panBoard} onPointerUp={endBoardPan} onPointerCancel={endBoardPan}>
				{boardColumns.map((column) => {
					const columnCards = displayedCards.filter((card) => String(card.columnId) === String(column.id));
					return <Paper key={column.id} variant="outlined" className={classes.column} onDragOver={(event) => event.preventDefault()} onDrop={(event) => moveCard(event.dataTransfer.getData("text/plain"), column.id)}>
						<div className={classes.columnHeader}>
							<div className={classes.columnTitle}>
								<LabelOutlined color="primary" />
								<Typography className={classes.columnName}>{column.name}</Typography>
							</div>
							<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
								<span className={classes.count}>{columnCards.length}</span>
								{!column.fixed && <Tooltip title="Editar etiqueta"><IconButton size="small" aria-label={`Editar ${column.name}`} onClick={() => editColumn(column)}><Edit fontSize="small" /></IconButton></Tooltip>}
							</div>
						</div>
						<div className={classes.cards}>{columnCards.length ? columnCards.map(renderCard) : <div className={classes.empty}><ChatBubbleOutline fontSize="large" /><Typography variant="body2">Vacío</Typography></div>}</div>
						{!column.fixed && <form className={classes.addForm} onSubmit={(event) => createCard(event, column.id)}>
							<TextField select size="small" fullWidth value={newCardContact[column.id] || ""} onChange={(event) => setNewCardContact({ ...newCardContact, [column.id]: event.target.value })} displayEmpty>
								<MenuItem value="">Vincular cliente o crear tarea</MenuItem>
								{contacts.map((contact) => <MenuItem value={contact.id} key={contact.id}>{contact.name}</MenuItem>)}
							</TextField>
							<div className={classes.addRow}>
								<TextField size="small" fullWidth placeholder="Agregar cliente o tarea" value={newCard[column.id] || ""} onChange={(event) => setNewCard({ ...newCard, [column.id]: event.target.value })} />
								<IconButton className={classes.addButton} type="submit" color="primary" aria-label="Agregar tarjeta"><Add /></IconButton>
							</div>
						</form>}
					</Paper>;
				})}
			</div>
		</div>
	</MainContainer>;
};

export default Kanban;
